import { CircularProgress, DialogTitle, useTheme } from '@mui/material';
import { ContextForm, FormContext, ILookup, InputOnlyField, Select } from '@ngt/forms';
import { IFormState } from '@ngt/forms-core';
import { FC, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SubStages } from '../api/dtos';
import usePricelistDownloadByStageIdSubstage from '../hooks/data/usePricelistDownloadByStageIdSubstage';
import usePricelistSubstagesByStageId from '../hooks/data/usePricelistSubstagesByStageId';
import useStagePricelistByStageIdSubStage from '../hooks/data/useStagePricelistByStageIdSubStage';
import { IFormDialogProps, StyledButton, StyledDialog, StyledDialogActions, StyledDialogContent } from './inputs/CommonFormElements';
import { enqueueSnackbar } from 'notistack';

interface ISubStageSelectProps {
    subStages?: SubStages[]
}

const SubStageSelect: FC<ISubStageSelectProps> = ({
    subStages
}) => {
    const params = useParams();
    const { validating, loading} = useContext(FormContext);
    const navigate = useNavigate();

    const substageFilterFunction = (_formState: IFormState, _propertyPath: string, lookup: Partial<ILookup> | undefined) => {
        return {
            propertyName: lookup?.propertyName,
            items: subStages ? lookup?.items?.filter(x => subStages.includes(x.id as SubStages)) : lookup?.items
        };
    }

    return (
        <InputOnlyField 
            label="SubStage"
            component={Select}
            LookupFilterProps={{
                filterFunction: substageFilterFunction, 
                subscription: {
                    initialValue: true,
                }
            }}
            propertyPath={'subStage'}
            onChange={(_e, _c, item) => {
                const subStage = item?.id

                if (params.subStage != subStage && !validating && !loading) {

                    if (subStage) {
                        navigate(`/projects/${params.projectId}/stages/${params.stageId}/download-pricelist/${subStage}`, {replace: true});
                    }
                    else {
                        navigate(`/projects/${params.projectId}/stages/${params.stageId}/download-pricelist`, {replace: true});
                    }
                }
            }}
        />
    );
}

const StagePricelistDownloadForm: FC<IFormDialogProps> = (props) => {
    const theme = useTheme();

    const params = useParams();

    const stageId = params.stageId ? Number(params.stageId) : undefined;
    const subStage = params.subStage ? Number(params.subStage) as SubStages : undefined;

    const {data: options, loading: substagesLoading} = usePricelistSubstagesByStageId(stageId);
    const {data: pricelist, loading: pricelistLoading} = useStagePricelistByStageIdSubStage(stageId, subStage);
    const download = usePricelistDownloadByStageIdSubstage();
    
    const handleDownload = async () => {
        try {
            const blob = await download(stageId, subStage);
            const url = window.URL.createObjectURL(blob);
    
            const link = document.createElement("a");
            link.href = url;
            link.download = `${pricelist?.name}.pdf`;
            link.click();
    
            window.URL.revokeObjectURL(url);
        }
        catch(e) {
            enqueueSnackbar("Failed to download pricelist", {variant: "error"});
        }
        finally {
            props.onClose()
        }
    }

    return (
        <>
            {
                substagesLoading || pricelistLoading ?
                <CircularProgress /> :
                <ContextForm>
                    <StyledDialog {...props}>
                        <DialogTitle
                            sx={{
                                backgroundColor: theme.palette.secondary.light,
                                color: "white"
                            }}
                        >
                            Select Substage
                        </DialogTitle>
                        <StyledDialogContent>
                            <SubStageSelect 
                                subStages={options}
                            />
                        </StyledDialogContent>
                        <StyledDialogActions>
                            <StyledButton variant="contained" onClick={handleDownload}>Download</StyledButton>
                            <StyledButton variant="contained" onClick={props.onClose}>Cancel</StyledButton>
                        </StyledDialogActions>
                    </StyledDialog>
                </ContextForm>
            }
        </>
    );
}

export default StagePricelistDownloadForm;